import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

/**
 * @description Enhanced SweetAlert2 instance with React content support
 */
const SwalAlert = withReactContent(Swal);

/**
 * @description Props for the fireAlert function
 * @interface FireAlertProps
 */
export interface FireAlertProps {
  /** The title of the alert */
  title: string;
  /** Optional HTML content for the alert body */
  html?: string;
}

/**
 * @description Props for the firePreConfirmAlert function
 * @interface FirePreConfirmAlertProps
 */
export interface FirePreConfirmAlertProps {
  /** The title of the alert */
  title: string;
  /** Optional HTML content for the alert body */
  html?: string;
  /** Function to execute when confirm button is clicked */
  preConfirm: any;
  /** Whether to focus on the cancel button by default */
  focusCancelBtn?: boolean;
}

/**
 * @description Displays a customized SweetAlert2 dialog with confirm and cancel options
 *
 * @SADtrace SAD-003-2.1, SAD-003-3.1, SAD-003-4.1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {FireAlertProps} options - The alert configuration options
 *
 * @returns {Promise} A promise that resolves with the user's response
 */
export const fireAlert = ({ title, html }: FireAlertProps) => {
  return SwalAlert.fire({
    title,
    html,
    confirmButtonColor: '#0895DF',
    cancelButtonText: 'Cancel',
    confirmButtonText: 'Confirm',
    showCancelButton: true,
    showCloseButton: true,
  });
};

/**
 * @description Displays a customized SweetAlert2 dialog with pre-confirmation functionality.
 * Executes a function before confirming the action, useful for delete confirmations with API calls.
 *
 * @SADtrace SAD-003-2.1, SAD-003-3.1, SAD-003-4.1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {FirePreConfirmAlertProps} options - The alert configuration options
 * @param {string} options.title - The title of the alert
 * @param {string} [options.html] - Optional HTML content for the alert body
 * @param {Function} options.preConfirm - Function to execute when confirm button is clicked
 * @param {boolean} [options.focusCancelBtn=true] - Whether to focus on the cancel button by default
 *
 * @returns {Promise} A promise that resolves with the user's response
 */
export const firePreConfirmAlert = ({
  title,
  html,
  preConfirm,
  focusCancelBtn = true,
}: FirePreConfirmAlertProps) => {
  return SwalAlert.fire({
    title,
    html,
    confirmButtonColor: '#0895DF',
    cancelButtonText: 'Cancel',
    confirmButtonText: 'Confirm',
    showCancelButton: true,
    focusCancel: focusCancelBtn,
    showCloseButton: true,
    showLoaderOnConfirm: false,
    preConfirm,
  });
};

export default SwalAlert;
